import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/lib/motion";
import { ArrowRight } from "lucide-react";
import { useSiteContent } from "@/lib/useSiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FinalCTA({ prefillMessage }) {
  const c = useSiteContent().cta;
  const budgets = c.budgets || [];
  const objectives = c.objectives || [];
  const consultations = c.consultations || [];

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    investment_budget: budgets[1] || "",
    investment_objective: objectives[1] || "",
    preferred_consultation: consultations[0] || "",
    message: prefillMessage || "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefillMessage) setForm((f) => ({ ...f, message: prefillMessage }));
  }, [prefillMessage]);

  // Keep dropdown defaults in sync if content loads late
  useEffect(() => {
    setForm((f) => ({
      ...f,
      investment_budget: f.investment_budget || budgets[1] || "",
      investment_objective: f.investment_objective || objectives[1] || "",
      preferred_consultation: f.preferred_consultation || consultations[0] || "",
    }));
  }, [budgets.length, objectives.length, consultations.length]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);

      // Fire Google Ads conversion on confirmed lead submission
    if (typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
       send_to: "AW-17798558952/Tt7qCL7CmtUcEOjpgadC",
        value: 1.0,
        currency: "AED",
      });
    }
      toast.success("Thank you. A senior advisor will be in touch within 24 hours.");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        country: "",
        investment_budget: budgets[1] || "",
        investment_objective: objectives[1] || "",
        preferred_consultation: consultations[0] || "",
        message: "",
      });
    } catch {
      toast.error("Unable to submit. Please try again or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="cta-section" className="relative section-pad bg-[#1D1D1F] text-white overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url('${c.background_image}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1D1D1F]/85 via-[#1D1D1F]/80 to-[#1D1D1F]/95" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <Reveal><p className="eyebrow text-[#C9A469]">{c.eyebrow}</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif-luxe text-4xl md:text-5xl leading-[1.08] mt-5">
              {c.heading}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/70 mt-6 leading-relaxed max-w-md">{c.body}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-3 text-sm text-white/60">
              {c.whatsapp_url && (
                <a href={c.whatsapp_url} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="link-gold inline-flex items-center gap-2">
                  {c.whatsapp_label}
                </a>
              )}
              {c.instagram_url && (
                <div>
                  <a href={c.instagram_url} target="_blank" rel="noreferrer" data-testid="cta-instagram" className="link-gold inline-flex items-center gap-2">
                    {c.instagram_label}
                  </a>
                </div>
              )}
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-x-8 gap-y-8" data-testid="lead-form">
              <div>
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-white/50">Full Name</label>
                <input required value={form.full_name} onChange={update("full_name")} className="input-luxe on-dark" data-testid="lead-name" />
              </div>
              <div>
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-white/50">Email Address</label>
                <input required type="email" value={form.email} onChange={update("email")} className="input-luxe on-dark" data-testid="lead-email" />
              </div>
              <div>
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-white/50">Phone Number</label>
                <input required value={form.phone} onChange={update("phone")} className="input-luxe on-dark" data-testid="lead-phone" />
              </div>
              <div>
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-white/50">Country of Residence</label>
                <input required value={form.country} onChange={update("country")} className="input-luxe on-dark" data-testid="lead-country" />
              </div>
              <div>
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-white/50">Investment Budget</label>
                <select value={form.investment_budget} onChange={update("investment_budget")} className="input-luxe on-dark bg-transparent" data-testid="lead-budget">
                  {budgets.map((b) => <option key={b} className="bg-[#1D1D1F]">{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-white/50">Investment Objective</label>
                <select value={form.investment_objective} onChange={update("investment_objective")} className="input-luxe on-dark bg-transparent" data-testid="lead-objective">
                  {objectives.map((b) => <option key={b} className="bg-[#1D1D1F]">{b}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-white/50">Preferred Consultation</label>
                <div className="flex flex-wrap gap-3 mt-3" data-testid="lead-consult-group">
                  {consultations.map((cs) => (
                    <button
                      key={cs}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, preferred_consultation: cs }))}
                      className={`px-5 py-3 text-[0.75rem] tracking-[0.14em] uppercase border transition-all ${
                        form.preferred_consultation === cs
                          ? "bg-[#B68D40] border-[#B68D40] text-white"
                          : "border-white/25 text-white/70 hover:border-white/60"
                      }`}
                      data-testid={`lead-consult-${cs.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {cs}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-white/50">Additional Notes (optional)</label>
                <textarea rows={3} value={form.message} onChange={update("message")} className="input-luxe on-dark resize-none" data-testid="lead-message" />
              </div>
              <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-6 mt-4">
                <p className="text-xs text-white/40 max-w-md leading-relaxed">{c.privacy_statement}</p>
                <button type="submit" disabled={loading} className="btn-luxe" data-testid="lead-submit">
                  {loading ? "Submitting…" : (<>{c.submit_label} <ArrowRight size={16} strokeWidth={1.4} /></>)}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
